import { Inject, Injectable, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../auth/auth/auth.service';
import { AppConfigService } from '../../shared/appconfig/appconfig.service'
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

import { NameValueDescription } from '../../shared/models/core.model';
import {
  Bot, BotTemplate, BotCommand, BotChannel, BotAudience, BotAudienceChannel, BotConversationMap,
  BotConnectorQuery, BotTrigger, BotRole, BotConnectorAction, BotSetting, BotSetupOptions,
  BotDialogOverride, BotChannelWatcher, BotChannelWatcherAction, BotSkill, AdminConfiguration,
  ModelResetAssessment, ModelResetResult
} from '../models/bot.model';
import { Connector, ConnectorDefinition } from '../models/connector.model';
import { Conversation, Message, UxItem, Attachment, BotVisuals } from '../models/chatstyle.model';
import { WIDGET_THEME_LIGHT } from '../models/widget-theme';
import { Series, ToDo, DashboardSummary } from '../../shared/models/core.model';
import { DialogEvent } from '../models/dialogs.model';

@Injectable({
  providedIn: 'root'
})
export class BotService {

  /**
   * Gets the admin configuration for a specific channel.
   */
  getChannelAdminConfiguration(botChannelId: string): Observable<string> {
    return this.http.get<string>(
      this.getUrl() + `botchannel/${botChannelId}/adminconfig`
    );
  }

  /**
   * Sets the admin configuration for a specific channel.
   */
  setChannelAdminConfiguration(botChannelId: string, config: any): Observable<string> {
    return this.http.post<string>(
      this.getUrl() + `botchannel/${botChannelId}/adminconfig`,
      config
    );
  }

  /**
   * Downloads the Teams manifest for a specific channel.
   * @param botChannelId Bot channel ID
   * @param channelType Channel type: 'client' or 'service'
   */
  getChannelManifest(botChannelId: string, channelType: string = 'service'): Observable<Blob> {
    return this.http.get(
      this.getUrl() + `botchannel/channel/${botChannelId}/manifest?channelType=${encodeURIComponent(channelType)}`,
      { responseType: 'blob' }
    );
  }

  getChannelTenants(botChannelId: string, skip = 0, take = 25, searchText = '', status = ''): Observable<{ total: number; items: any[] }> {
    let url = this.getUrl() + `botchannel/channel/${encodeURIComponent(botChannelId)}/tenants?skip=${skip}&take=${take}`;
    if (searchText) url += `&searchText=${encodeURIComponent(searchText)}`;
    if (status) url += `&status=${encodeURIComponent(status)}`;
    return this.http.get<{ total: number; items: any[] }>(url);
  }

  getMultiTenantApprovalUrl(botChannelId: string, redirectUri: string, state: string): Observable<string> {
    const params = new URLSearchParams();
    params.append('redirectUri', redirectUri);
    params.append('state', state);
    params.append('channelId', botChannelId);
    params.append('isMultiTenant', 'true');
    const url = this.getUrl() + `bots/teams/approvalUrl/standard?${params.toString()}`;
    return this.http.get(url, { responseType: 'text' });
  }

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject('BASE_URL') baseUrl: string) {
  }

  private botsSubject = new BehaviorSubject<Bot[]>([]);
  public bots$ = this.botsSubject.asObservable();
  private botsLoaded = false;
  public useCache = true;

  private botSubject = new BehaviorSubject<Bot | null>(null);
  public bot$ = this.botSubject.asObservable();
  private botLoaded = false;
  private botInflight$: Observable<Bot> | null = null;

  get bots(): Bot[] {
    return this.botsSubject.getValue();
  }

  getUrl(): string {
    return AppConfigService.settings.get('chatstyleapiServer');
  }

  emptyBot(): Bot {
    return {
      botID: '',
      description: '',
      audienceCount: 0,
      imageUrl: '',
      name: '',
      active: true,
      category: '',
      state: '',
      configurationType: '',
      maintenanceDelay: 0,
      timeZoneOffset: -300,
      dialogOwnerId: ''
    };
  };

  emptyMessage(conversationID: string, botID: string, senderID: string): Message {

    if (this.authService) {
      if (this.authService.isLoggedIn()) {
        senderID = this.authService.getClaims().sub;
      }
    }

    return {
      attachments: [],
      body: "",
      channel: "signalr",
      conversationID: conversationID,
      recipientID: botID,
      senderID: senderID,
      payload: "",
      text: "",
      time: new Date(),
      uxItems: [],
      fromDisplayName: '',
      toDisplayName: '',
      tag: '',
      info: '',
      blockInput: false,
      blockAttachments: false,
      ttl: ''
    };
  }

  newId(): Observable<string> {

    return this.http.get(this.getUrl() + 'bots/newid', { responseType: 'text' });

  }

  getBots(): Observable<Bot[]> {
    if (this.useCache && this.botsLoaded) {
      return of(this.botsSubject.getValue());
    }

    return this.http.get<Bot[]>(this.getUrl() + 'bots').pipe(
      tap(bots => {
        this.botsSubject.next(bots);
        this.botsLoaded = true;
      })
    );
  }

  refreshBots(): Observable<Bot[]> {
    this.botsLoaded = false;
    return this.getBots();
  }

  getBotTemplates(): Observable<BotTemplate[]> {
    return this.http.get<BotTemplate[]>(this.getUrl() + 'bots/templates');
  }

  /**
   * Loads (or returns the cached) per-tenant bot. Pushes into `bot$` on success so
   * any subscriber to the subject sees the value. Concurrent callers share a
   * single in-flight HTTP request via `shareReplay(1)`; once the request completes
   * `finalize` clears the inflight reference so a future `refreshBot()` can re-fetch.
   */
  getBot(): Observable<Bot> {
    if (this.useCache && this.botLoaded && this.botSubject.value) {
      return of(this.botSubject.value);
    }
    if (this.botInflight$) {
      return this.botInflight$;
    }
    this.botInflight$ = this.http.get<Bot>(this.getUrl() + 'bots/current').pipe(
      tap((bot) => {
        this.botSubject.next(bot);
        this.botLoaded = true;
      }),
      shareReplay(1),
      finalize(() => {
        this.botInflight$ = null;
      })
    );
    return this.botInflight$;
  }

  refreshBot(): Observable<Bot> {
    this.botLoaded = false;
    this.botInflight$ = null;
    return this.getBot();
  }

  getBotConfiguration(): Observable<string> {
    return this.http.get<string>(this.getUrl() + 'bots/configuration');
  }

  setBotConfiguration(config: any): Observable<string> {
    return this.http.post<string>(this.getUrl() + 'bots/configuration', config);
  }

  getBotAdminConfiguration(): Observable<AdminConfiguration> {
    return this.http.get<AdminConfiguration>(
      this.getUrl() + 'bots/admin/configuration'
    );
  }

  setBotAdminConfiguration(config: any): Observable<string> {
    return this.http.post<string>(this.getUrl() + 'bots/admin/configuration', config);
  }

  // CHAT-1117: SuperAdmin model reset. The assessment writes nothing.
  getModelResetAssessment(): Observable<ModelResetAssessment> {
    return this.http.get<ModelResetAssessment>(this.getUrl() + 'admin/model-reset/assessment');
  }

  resetBotToModel(expectedBotId: number): Observable<ModelResetResult> {
    return this.http.post<ModelResetResult>(this.getUrl() + 'admin/model-reset', { expectedBotId });
  }

  setBotDialogRoot(dialogGraphId: string): Observable<Bot> {
    return this.http.post<Bot>(this.getUrl() + 'bots/dialogroot/' + dialogGraphId, dialogGraphId);
  }

  getApiKey(): Observable<string> {
    return this.http.get<string>(this.getUrl() + 'bots/apiKey');
  }

  getSettings(): Observable<BotSetting[]> {
    return this.http.get<BotSetting[]>(this.getUrl() + 'bots/settings');
  }

  getSetup(): Observable<ToDo[]> {
    return this.http.get<ToDo[]>(this.getUrl() + 'bots/setup');
  }

  getUserSettings(): Observable<BotSetting[]> {
    return this.http.get<BotSetting[]>(this.getUrl() + 'bots/usersettings');
  }

  getMetrics(days: number): Observable<Series[]> {
    return this.http.get<Series[]>(this.getUrl() + 'bots/metrics?days=' + days.toString());
  }

  getDashboard(days: number): Observable<any> {
    return this.http.get<any>(this.getUrl() + 'bots/dashboard?days=' + days.toString());
  }

  getVisuals(): Observable<BotVisuals> {
    return this.http.get<BotVisuals>(this.getUrl() + 'bots/visuals');
  }

  getRoles(): Observable<BotRole[]> {
    return this.http.get<BotRole[]>(this.getUrl() + 'bots/roles');
  }

  getTriggers(): Observable<BotTrigger[]> {
    return this.http.get<BotTrigger[]>(this.getUrl() + 'bots/triggers');
  }

  getDialogEvents(): Observable<DialogEvent[]> {
    return this.http.get<DialogEvent[]>(this.getUrl() + 'bots/dialogevents');
  }

  updateDialogEvents(events: DialogEvent[]): Observable<DialogEvent[]> {
    return this.http.post<DialogEvent[]>(this.getUrl() + 'bots/dialogevents', events);
  }

  getActions(): Observable<BotConnectorAction[]> {
    return this.http.get<BotConnectorAction[]>(this.getUrl() + 'bots/actions');
  }

  getQueries(): Observable<BotConnectorQuery[]> {
    return this.http.get<BotConnectorQuery[]>(this.getUrl() + 'bots/queries');
  }

  getChannels(): Observable<BotChannel[]> {
    return this.http.get<BotChannel[]>(this.getUrl() + 'botchannel');
  }

  getChannelConnectors(channel: string): Observable<Connector[]> {
    return this.http.get<Connector[]>(this.getUrl() + 'botchannel/connectors/' + channel);
  }

  getPsaConnectors(): Observable<Connector[]> {
    return this.http.get<Connector[]>(this.getUrl() + 'botchannel/psa-connectors');
  }

  getCommands(): Observable<BotCommand[]> {
    return this.http.get<BotCommand[]>(this.getUrl() + 'botcommand');
  }

  getWatchers(): Observable<BotChannelWatcher[]> {
    return this.http.get<BotChannelWatcher[]>(this.getUrl() + 'watcher');
  }

  getDialogOverrides(): Observable<BotDialogOverride[]> {
    return this.http.get<BotDialogOverride[]>(this.getUrl() + 'botdialogoverride');
  }

  getConnectors(): Observable<ConnectorDefinition[]> {
    return this.http.get<ConnectorDefinition[]>(this.getUrl() + 'bots/connectors');
  }

  getConnector(id: string): Observable<ConnectorDefinition> {
    return this.http.get<ConnectorDefinition>(this.getUrl() + 'connectors/' + id);
  }

  getConnectorsByType(type: string, subtype: string, availableFor: string): Observable<ConnectorDefinition[]> {
    let avail = '';
    if (availableFor) {
      avail = '?available=' + availableFor;
    }
    return this.http.get<ConnectorDefinition[]>(this.getUrl() + 'bots/connectors/' + type + '/' + subtype + avail);
  }

  getAvailableConnectorsByType(type: string, subtype: string): Observable<ConnectorDefinition[]> {
    return this.http.get<ConnectorDefinition[]>(this.getUrl() + 'bots/connectors/available/' + type + '/' + subtype);
  }

  getConversations(): Observable<Conversation[]> {
    return this.http.get<Conversation[]>(this.getUrl() + 'bots/conversations');
  }

  getChannel(id: string): Observable<BotChannel> {
    return this.http.get<BotChannel>(this.getUrl() + 'botchannel/type/' + id);
  }

  getChannelById(id: number | string): Observable<BotChannel> {
    return this.http.get<BotChannel>(this.getUrl() + 'botchannel/' + id);
  }

  getChannelContent(channel: string, id: string): Observable<string> {
    return this.http.get(this.getUrl() + 'botchannel/content/' + channel + '/' + id,
      { responseType: 'text' });
  }

  getCommand(command: string): Observable<BotCommand> {
    return this.http.get<BotCommand>(this.getUrl() + 'botcommand/' + command);
  }

  getCommandById(source: string, id: number | string): Observable<BotCommand> {
    return this.http.get<BotCommand>(
      this.getUrl() +
        'botcommand/' +
        encodeURIComponent(source) +
        '/' +
        encodeURIComponent(String(id))
    );
  }

  getWatcher(id: string): Observable<BotChannelWatcher> {
    return this.http.get<BotChannelWatcher>(this.getUrl() + 'watcher/' + id);
  }

  getDialogOverride(overrideId: string): Observable<BotDialogOverride> {
    return this.http.get<BotDialogOverride>(this.getUrl() + 'botdialogoverride/' + overrideId);
  }

  deleteChannel(id: string): Observable<string> {
    return this.http.delete(this.getUrl() + 'botchannel/' + id,
      { responseType: 'text' });
  }

  deleteCommand(command: string): Observable<string> {
    return this.http.delete(this.getUrl() + 'botcommand/' + command,
      { responseType: 'text' });
  }

  deleteWatcher(id: string): Observable<string> {
    return this.http.delete(this.getUrl() + 'watcher/' + id,
      { responseType: 'text' });
  }

  deleteDialogOverride(overrideId: string): Observable<string> {
    return this.http.delete(this.getUrl() + 'botdialogoverride/' + overrideId,
      { responseType: 'text' });
  }

  emptyChannel(): BotChannel {

    const channel: BotChannel = {
      id: 0,
      botID: '',
      name: '',
      description: '',
      active: true,
      channel: '',
      channelUniqueID: '',
      connectorID: 0,
      data: '',
      role: '',
      maintenanceDelay: 0,
      options: '',
      companyName: '',
      fromTemplate: false,
      channelContent: '',
      isVerified: false,
      allowedUrlList: ''
    };
    return channel;
  }

  emptyRole(): BotRole {
    const r: BotRole = {
      targetDialogGraphID: '',
      targetDialogNodeID: '',
      ownerID: '',
      role: '',
      note: '',
      description: '',
      conditionScript: '',
      active: true,
      initialState: '',
      aiEnabled: false,
      commandEnabled: false
    };
    return r;
  };

  emptyCommand(): BotCommand {

    const cmd: BotCommand = {
      id: 0,
      botID: '',
      name: '',
      description: '',
      active: true,
      language: 'javascript',
      role: '',
      commandVersion: '',
      helpText: '',
      sampleScript: '',
      commandScript: '',
      fromTemplate: false,
      global: false,
      readOnly: false,
      visible: true,
      scriptVisible: true,
      parametersJson: '',
      shortName: '',
      addToPSA: false,
      category: '',
      aiEnabled: false,
      aiDescription: '',
      aiPriority: 0,
      isShared: false
    };
    return cmd;
  }


  emptyQuery(): BotConnectorQuery {

    const q: BotConnectorQuery = {
      name: '',
      connectorID: 0,
      connectorDefinitionID: '',
      botID: '',
      returnType: '',
      queryType: '',
      queryScript: '',
      orderBy: '',
      active: true,
      cacheExpiry: 60,
      uniqueID: ''
    };
    return q;
  }


  emptyWatcher(): BotChannelWatcher {

    const w: BotChannelWatcher = {
      botID: '',
      internalID: '',
      externalID: '',
      name: '',
      description: '',
      actions: [],
      actionsJson: '',
      connectorUserID: '',
      connectorID: 0,
      connectorDefinitionID: '',
      connectorExternalID: '',
      watchBotChannelId: '',
      watchGroupId: '',
      watchGroupName: '',
      watchChannelId: '',
      watchChannelName: '',
      watchThreadId: '',
      synch: false,
      synchChannel: '',
      synchBotChannelId: '',
      synchGroupId: '',
      synchGroupName: '',
      synchChannelId: '',
      synchChannelName: '',
      active: true,
      readOnly: false,
      ignoreBotMentions: true,
      channelToWatch: null,
      teamToWatch: null,
      channelToSynch: null,
      teamToSynch: null
    };
    return w;
  }

  emptyWatcherAction(): BotChannelWatcherAction {

    const w: BotChannelWatcherAction = {
      Action: '',
      ActionQueue: '',
      Tag: '',
      Behavior: '',
      Data: '',
      UseConversation: true,
      Active: true,
      Match: []
    };
    return w;
  }

  emptyDialogOverride(): BotDialogOverride {

    const ovr: BotDialogOverride = {
      botID: '',
      overrideID: '',
      name: '',
      description: '',
      active: true,
      role: '',
      script: '',
      shortName: ''
    };
    return ovr;
  }

  emptyAudience(): BotAudience {

    const ba: BotAudience = {
      botID: '',
      audienceID: '',
      firstName: '',
      lastName: '',
      profilePicUrl: '',
      email: '',
      phone: '',
      hasLocation: false,
      latitude: -1,
      longitude: -1,
      role: '',
      externalID: '',
      active: true
    };
    return ba;
  }

  emptyAction(): BotConnectorAction {

    const a: BotConnectorAction = {
      name: '',
      connectorID: 0,
      connectorDefinitionID: '',
      action: '',
      type: '',
      actionScript: '',
      conditionScript: '',
      active: true,
      language: 'javascript'
    };
    return a;
  }

  emptyVisuals(): BotVisuals {

    const v: BotVisuals = {
      label: '',
      description: '',
      imageUrl: '',
      iconUrl: '',
      primaryColor: '',
      theme: WIDGET_THEME_LIGHT,
    };
    return v;
  }

  emptyAudienceChannel(): BotAudienceChannel {

    const bac: BotAudienceChannel = {
      botID: '',
      audienceID: '',
      active: false,
      channel: '',
      channelUniqueID: '',
      botChannelUniqueID: '',
      name: '',
      isParent: false
    };
    return bac;
  }

  getAudience(): Observable<BotAudience[]> {
    return this.http.get<BotAudience[]>(this.getUrl() + 'bots/audience');
  }

  editAudience(id: string): Observable<BotAudience> {
    return this.http.get<BotAudience>(this.getUrl() + 'bots/audience/' + id);
  }

  resetAudienceConversation(audienceID: string): Observable<Object> {
    return this.http.post<Object>(this.getUrl() + 'bots/audience/' + audienceID + '/resetConversation', {});
  }


  editAction(name: string): Observable<BotConnectorAction> {
    return this.http.get<BotConnectorAction>(this.getUrl() + 'bots/actions/' + name);
  }

  editQuery(name: string): Observable<BotConnectorQuery> {
    return this.http.get<BotConnectorQuery>(this.getUrl() + 'bots/queries/' + name);
  }


  setBot(bot: Bot): Observable<Bot> {
    return this.http.post<Bot>(this.getUrl() + 'bots', bot).pipe(
      tap((updated) => {
        this.botSubject.next(updated);
        this.botLoaded = true;
      })
    );
  }

  setAudience(audience: BotAudience): Observable<BotAudience> {
    return this.http.post<BotAudience>(this.getUrl() + 'bots/audience', audience);
  }

  setAction(action: BotConnectorAction): Observable<BotConnectorAction> {
    return this.http.post<BotConnectorAction>(this.getUrl() + 'bots/actions', action);
  }

  setChannel(channel: BotChannel): Observable<BotChannel> {
    return this.http.post<BotChannel>(this.getUrl() + 'botchannel', channel);
  }

  setCommand(command: BotCommand): Observable<BotCommand> {
    return this.http.post<BotCommand>(this.getUrl() + 'botcommand', command);
  }

  setWatcher(watcher: BotChannelWatcher): Observable<BotChannelWatcher> {
    return this.http.post<BotChannelWatcher>(this.getUrl() + 'watcher', watcher);
  }

  setQuery(query: BotConnectorQuery): Observable<BotConnectorQuery> {
    return this.http.post<BotConnectorQuery>(this.getUrl() + 'bots/queries', query);
  }

  setDialogOverride(dovr: BotDialogOverride): Observable<BotDialogOverride> {
    return this.http.post<BotDialogOverride>(this.getUrl() + 'botdialogoverride', dovr);
  }

  setSettings(settings: BotSetting[]): Observable<BotSetting[]> {
    return this.http.post<BotSetting[]>(this.getUrl() + 'bots/settings', settings);
  }

  setRoles(settings: BotRole[]): Observable<BotRole[]> {
    return this.http.post<BotRole[]>(this.getUrl() + 'bots/roles', settings);
  }

  setVisuals(visuals: BotVisuals): Observable<BotVisuals> {
    return this.http.post<BotVisuals>(this.getUrl() + 'bots/visuals', visuals);
  }

  /*
  getSupport(id: string): Promise<BotAudienceChannel[]> {

    return this.http.get<BotAudienceChannel[]>(this.getUrl() + 'bots/' + id +
      '/support/all').toPromise();

  }

  setSupport(audience: BotAudienceChannel): Promise<BotAudienceChannel> {
    return this.http
      .post<BotAudienceChannel>(this.getUrl() + 'bots/' + audience.botID +
        '/support/all',
        audience).toPromise();
  }
  */

  createBot(options: BotSetupOptions): Observable<Bot> {
    return this.http.put<Bot>(this.getUrl() + 'bots/', options);
  }

  getMaps(): Observable<BotConversationMap[]> {
    return this.http.get<BotConversationMap[]>(this.getUrl() + 'bots/conversationmaps');
  }

  updateMaps(maps: BotConversationMap[]): Observable<BotConversationMap[]> {
    return this.http.post<BotConversationMap[]>(this.getUrl() + 'bots/conversationmaps', maps);
  }

  resetConversation(channel: string): Observable<Object> {
    return this.http.post(this.getUrl() + 'conversation/' +
      channel + "/signalr/" + this.authService.getClaims().sub + "/reset",
      "");
  }

  restartConversation(channel: string): Observable<Object> {
    return this.http.post(this.getUrl() + 'conversation/' +
      channel + "/signalr/" + this.authService.getClaims().sub + "/restart",
      "");
  }

  debugConversation(channel: string): Observable<Object> {
    return this.http.post(this.getUrl() + 'conversation/' +
      channel + "/signalr/" + this.authService.getClaims().sub + "/debug/true",
      "");
  }

  closeDebugConversation(botId: string, conversationId: string): Promise<Object> {
    return this.http
      .post(this.getUrl() + `conversation/${botId}/${conversationId}/debug/false`, '')
      .toPromise();
  }

  startDebugFromGraph(channel: string, startGraphID: string, startNodeID: string,
      parameters: Record<string, string>, conversationID?: string): Promise<{ conversationID: string }> {
    return this.http
      .post<{ conversationID: string }>(
        this.getUrl() + 'conversation/' + channel + '/signalr/' +
          this.authService.getClaims().sub + '/debug/start',
        { startGraphID, startNodeID, parameters, conversationID }
      ).toPromise();
  }

  setConnector(connector: Connector): Promise<Connector> {
    return this.http
      .post<Connector>(this.getUrl() + 'bots/connectors',
        connector).toPromise();
  }

  /**
   * Gets the Slack approval URL for a channel, built server-side so the OAuth state carries the
   * tenant DB (CHAT-728). Replaces the client-side SlackChannelService.buildApprovalUrl, which
   * could not embed the tenant and caused "tenant could not be resolved" on the callback.
   */
  getSlackApprovalUrl(
    channelId: string | null,
    role: string,
    channelName: string,
    redirectUri: string
  ): Observable<string> {
    const params = new URLSearchParams();
    params.append('redirectUri', redirectUri);
    if (channelId) {
      params.append('channelId', channelId);
    }
    if (role) {
      params.append('role', role);
    }
    if (channelName) {
      params.append('channelName', channelName);
    }

    const url = this.getUrl() + `bots/slack/approvalUrl?${params.toString()}`;

    return this.http.get(url, { responseType: 'text' });
  }

  /**
   * Gets the Teams approval URL for a bot channel
   * @param channelId The channel ID (optional, for existing channels)
   * @param permissionLevel Permission level: 'standard' or 'advanced'
   * @param redirectUri The redirect URI after approval
   * @param state The state parameter for OAuth
   * @param usePopup Whether to use popup mode (adds popup flag to state)
   */
  getTeamsApprovalUrl(
    channelId: string | null,
    permissionLevel: string,
    redirectUri: string,
    state: string,
    usePopup: boolean = false
  ): Observable<string> {
    const params = new URLSearchParams();
    params.append('redirectUri', redirectUri);
    params.append('state', usePopup ? `${state}|popup` : state);
    if (channelId) {
      params.append('channelId', channelId);
    }

    const url = this.getUrl() + `bots/teams/approvalUrl/${permissionLevel}?${params.toString()}`;

    return this.http.get(url, { responseType: 'text' });
  }

  /**
   * Sends a channel approval invitation email with the approval link
   */
  sendChannelApprovalInvitation(
    channelId: string,
    delegateEmail: string,
    channelType: 'teams' | 'slack',
    approvalUrl: string
  ): Observable<any> {
    const url = this.getUrl() + `bots/channels/${channelId}/sendApprovalInvitation`;

    const payload = {
      delegateEmail,
      channelType,
      approvalUrl
    };

    return this.http.post(url, payload);
  }

  getSkills(botId: string): Promise<BotSkill[]> {
    return this.http.get<BotSkill[]>(this.getUrl() + 'botskill/' + encodeURIComponent(botId)).toPromise();
  }

  getSkill(botId: string, name: string): Promise<BotSkill> {
    return this.http.get<BotSkill>(this.getUrl() + 'botskill/' + encodeURIComponent(botId) + '/' + encodeURIComponent(name)).toPromise();
  }

  setSkill(skill: BotSkill): Promise<BotSkill> {
    return this.http.post<BotSkill>(this.getUrl() + 'botskill/' + encodeURIComponent(skill.botID), skill).toPromise();
  }

  deleteSkill(botId: string, name: string): Observable<string> {
    return this.http.delete(this.getUrl() + 'botskill/' + encodeURIComponent(botId) + '/' + encodeURIComponent(name), { responseType: 'text' });
  }

  emptySkill(): BotSkill {
    return {
      botID: '',
      name: '',
      shortName: '',
      description: '',
      category: '',
      helpText: '',
      active: true,
      aiEnabled: false,
      aiDescription: '',
      aiPrompt: '',
      parametersJson: '',
      aiPriority: 0,
      created: new Date().toISOString(),
      updated: new Date().toISOString()
    };
  }

}
